import type { ImportExcelActionConfig } from '@lighthouse/shared'
import { TooltipText } from '@lighthouse/shared'
import React, { useMemo } from 'react'

import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList, useDataSourceOptions } from '@/hooks/useDataSource'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import * as CM from '../styles'
import { TakeVariableWithText, useTakeVariableWithText } from '../TakeVariableWithText'
import * as SC from './styles'

type ImportExcelCardProps = {
    config: ImportExcelActionConfig
}

const ImportExcelCard: React.FC<ImportExcelCardProps> = ({ config }) => {
    const { dataSourceId, excelSource, repeat, requiredFields, compareFields, importMode } = config
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const dsOptions = useDataSourceOptions()
    // keep atom subscription consistent with other cards (no direct usage needed here)
    const userOption = useGetUserRecordOptions()

    const dsOption = useMemo(
        () => (dataSourceId ? dsOptions.find(item => item.value === dataSourceId) : undefined),
        [dataSourceId, dsOptions]
    )

    const excelSourceData = useTakeVariableWithText({ dataSourceList, value: excelSource })
    const upstreamOptions = useMemo(
        () => (excelSourceData.upstreamOptions ? excelSourceData.upstreamOptions : undefined),
        [excelSourceData.upstreamOptions]
    )

    const schema = useMemo(() => dataSourceList.find(ds => ds.id === dataSourceId)?.schema || {}, [dataSourceId, dataSourceList])

    const compareFieldName = useMemo(() => {
        if (!compareFields?.length) {
            return ''
        }
        const first = schema?.[compareFields[0]]
        return first?.name || compareFields[0]
    }, [compareFields, schema])

    const importModeLabel = useMemo(() => {
        switch (importMode) {
            case 'skip':
                return '跳过重复记录'
            case 'cover':
                return '仅更新，不新增记录'
            case 'update':
                return '仅更新，不新增记录'
            case 'error':
                return '导入报错'
            default:
                return ''
        }
    }, [importMode])

    if (!dsOption) {
        return null
    }

    return (
        <CM.CardItemContainer>
            <CM.FieldList>
                <SC.SectionLabel>目标导入数据表</SC.SectionLabel>
                <CM.FieldItem>
                    <CM.FieldItemLeftFill>
                        <SC.Icon type={dsOption.icon ?? ''} />
                        <TooltipText
                            title={dsOption.label}
                            render={ref => <CM.FieldItemTitle ref={ref}>{dsOption.label}</CM.FieldItemTitle>}
                        />
                    </CM.FieldItemLeftFill>
                </CM.FieldItem>

                <SC.SectionLabel style={{ marginTop: 8 }}>导入的 Excel</SC.SectionLabel>
                <CM.FieldItemContent>
                    <SC.RichTextPreview>
                        <TakeVariableWithText
                            nodes={excelSourceData.data.objects}
                            userOption={userOption}
                            upstreamOptions={upstreamOptions}
                        />
                    </SC.RichTextPreview>
                </CM.FieldItemContent>

                <SC.SectionLabel style={{ marginTop: 8 }}>导入设置</SC.SectionLabel>

                <CM.FieldItem>
                    <CM.FieldItemTitle>必填项</CM.FieldItemTitle>
                    <CM.FieldItemTitle color="var(--color-gray-900)">
                        {requiredFields?.length ? `已设置 ${requiredFields.length} 项` : '未设置'}
                    </CM.FieldItemTitle>
                </CM.FieldItem>

                <CM.FieldItem>
                    <CM.FieldItemTitle>识别重复性</CM.FieldItemTitle>
                    <SC.RightText $enabled={repeat}>{repeat ? '开启' : '关闭'}</SC.RightText>
                </CM.FieldItem>

                {repeat && (
                    <>
                        <CM.FieldItem>
                            <CM.FieldItemTitle>识别重复依据的字段</CM.FieldItemTitle>
                            <CM.FieldItemTitle color="var(--color-gray-900)">
                                {compareFieldName || (compareFields?.length ? `${compareFields.length} 项` : '未设置')}
                            </CM.FieldItemTitle>
                        </CM.FieldItem>
                        <CM.FieldItem>
                            <CM.FieldItemTitle>识别到重复记录时</CM.FieldItemTitle>
                            <CM.FieldItemTitle color="var(--color-gray-900)">{importModeLabel || '未设置'}</CM.FieldItemTitle>
                        </CM.FieldItem>
                    </>
                )}
            </CM.FieldList>
        </CM.CardItemContainer>
    )
}

export default ImportExcelCard
